import React, { useState, useRef, useEffect } from "react";
import { AnimatePresence, motion } from "motion/react";
import { ChevronDown, Check, Plus } from "lucide-react";
import AddSenderModal from "../../quickSms/AddSenderModal";

const STATUS_STYLES = {
  approved: { label: "Approved", className: "bg-green-50 text-green-700 border-green-200" },
  pending: { label: "Pending", className: "bg-yellow-50 text-yellow-700 border-yellow-200" },
  rejected: { label: "Rejected", className: "bg-red-50 text-red-700 border-red-200" },
};

function getStatusStyle(status) {
  return STATUS_STYLES[status?.toLowerCase()] || STATUS_STYLES.pending;
}

// A compact, modal-appropriate sender-ID dropdown - a lighter alternative to
// quickSms/SenderIdSelector.jsx. That component is a full self-contained
// "page section" (its own header, description, search bar, stats summary,
// all wrapped in its own bordered/shadowed box) built for spacious pages
// like QuickSms/BulkSms. Nesting it as-is inside a compact modal produced a
// box-within-a-box, and its responsive grid never actually kicked in since
// the modal is capped well under the breakpoints that would widen it.
// This is a closed-by-default dropdown instead: the trigger shows the
// current selection, opening it reveals the list, picking an option closes
// it back down - matching the toggle+panel dropdown pattern already used in
// Navbar.jsx's "More" menu.
const SenderPickerList = ({ selectedSenderId, onSelectSenderId, senderIds, isLoading, onAddSender }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [showAddSenderModal, setShowAddSenderModal] = useState(false);
  const dropdownContainerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownContainerRef.current && !dropdownContainerRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
      }
    };
    if (isDropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isDropdownOpen]);

  const selectedSender = senderIds.find((sender) => sender.sender === selectedSenderId);

  const handleSelect = (senderName) => {
    onSelectSenderId(senderName);
    setIsDropdownOpen(false);
  };

  if (isLoading) {
    return <div className="h-11 bg-gray-100 rounded-xl animate-pulse" />;
  }

  return (
    <div ref={dropdownContainerRef} className="relative">
      <button
        type="button"
        onClick={() => setIsDropdownOpen((currentlyOpen) => !currentlyOpen)}
        className="flex items-center justify-between gap-2 w-full px-3 py-2.5 text-sm bg-white border border-gray-200 rounded-xl transition-colors hover:border-red-200 focus:outline-none focus:ring-2 focus:ring-red-500"
      >
        {selectedSender ? (
          <span className="flex items-center gap-2 min-w-0">
            <span className="font-semibold text-gray-800 truncate">{selectedSender.sender}</span>
            <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-medium border ${getStatusStyle(selectedSender.status).className}`}>
              {getStatusStyle(selectedSender.status).label}
            </span>
          </span>
        ) : (
          <span className="text-gray-400">Select a sender ID</span>
        )}
        <ChevronDown className={`flex-shrink-0 w-4 h-4 text-gray-400 transition-transform ${isDropdownOpen ? "rotate-180" : ""}`}/>
      </button>

      <AnimatePresence>
        {isDropdownOpen && (
          <motion.div
            initial={{ opacity: 0, y: -8, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: -8, scale: 0.98 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden absolute left-0 right-0 z-20 mt-1.5 bg-white rounded-xl border border-gray-200 shadow-lg"
          >
            <div className="overflow-y-auto py-1 max-h-44">
              {senderIds.length === 0 ? (
                <p className="px-3 py-3 text-xs text-center text-gray-400">No sender IDs yet.</p>
              ) : (
                senderIds.map((sender) => {
                  const isApproved = sender.status?.toLowerCase() === "approved";
                  const isSelected = selectedSenderId === sender.sender;
                  return (
                    <button
                      key={sender.id}
                      type="button"
                      disabled={!isApproved}
                      onClick={() => handleSelect(sender.sender)}
                      title={!isApproved ? "Only approved sender IDs can be used" : undefined}
                      className={`flex items-center justify-between gap-2 w-full px-3 py-2 text-left transition-colors ${
                        isApproved ? "hover:bg-red-50 cursor-pointer" : "cursor-not-allowed opacity-60"
                      } ${isSelected ? "bg-red-50" : ""}`}
                    >
                      <span className="flex items-center gap-1.5 min-w-0">
                        {isSelected && <Check className="flex-shrink-0 w-3.5 h-3.5 text-red-600"/>}
                        <span className="text-sm font-medium text-gray-800 truncate">{sender.sender}</span>
                      </span>
                      <span className={`flex-shrink-0 px-2 py-0.5 rounded-full text-[10px] font-medium border ${getStatusStyle(sender.status).className}`}>
                        {getStatusStyle(sender.status).label}
                      </span>
                    </button>
                  );
                })
              )}
            </div>
            {/* <button
              type="button"
              onClick={() => { setShowAddSenderModal(true); setIsDropdownOpen(false); }}
              className="flex items-center gap-1.5 w-full px-3 py-2.5 text-xs font-semibold text-red-600 border-t border-gray-100 hover:bg-red-50"
            >
              <Plus className="w-3.5 h-3.5"/> Add a new sender ID
            </button> */}
          </motion.div>
        )}
      </AnimatePresence>

      {showAddSenderModal && (
        <AddSenderModal
          onClose={() => setShowAddSenderModal(false)}
          onAdd={onAddSender}
        />
      )}
    </div>
  );
};

export default SenderPickerList;
